import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { LogOut, FileCheck, DollarSign, Calendar, User, RefreshCw, Search, Plus, X, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ExitManagement({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('resignations');
  const [resignations, setResignations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedResignation, setSelectedResignation] = useState(null);
  const [showResignationForm, setShowResignationForm] = useState(false);
  const [showClearanceForm, setShowClearanceForm] = useState(false);
  const [showFnfForm, setShowFnfForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [resignationData, setResignationData] = useState({
    employee_id: '',
    resignation_date: new Date().toISOString().split('T')[0],
    last_working_date: '',
    reason: '',
    notice_period_days: 30,
    is_notice_served: true,
    notice_buyout: false,
    notice_buyout_amount: 0
  });

  const [clearanceData, setClearanceData] = useState({
    resignation_id: '',
    asset_return_status: 'pending',
    asset_return_notes: '',
    dues_cleared: false,
    dues_amount: 0,
    recoveries: 0,
    exit_interview_completed: false,
    exit_interview_notes: ''
  });

  const [fnfData, setFnfData] = useState({
    resignation_id: '',
    employee_id: '',
    basic_salary_days: 0,
    hra_days: 0,
    other_allowances: 0,
    pending_salary: 0,
    leave_encashment: 0,
    bonus_if_any: 0,
    notice_shortfall_recovery: 0,
    advance_recovery: 0,
    loan_recovery: 0,
    asset_damage_recovery: 0,
    other_deductions: 0,
    gross_amount: 0,
    total_deductions: 0,
    net_payable: 0,
    payment_status: 'pending'
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [resignationsRes, employeesRes] = await Promise.allSettled([
        axios.get(`${API}/exit-management/resignations`, { headers }),
        axios.get(`${API}/employees`, { headers })
      ]);

      setResignations(resignationsRes.status === 'fulfilled' ? (resignationsRes.value.data.resignations || []) : []);
      setEmployees(employeesRes.status === 'fulfilled' ? (employeesRes.value.data.filter(emp => emp.employee_status === 'Active') || []) : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter resignations
  const filteredResignations = resignations.filter(res => {
    const matchesSearch = !searchTerm || 
      res.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      res.employee_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      res.department?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || res.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Statistics
  const stats = {
    total: resignations.length,
    pending: resignations.filter(r => r.status === 'submitted' || r.status === 'pending').length,
    approved: resignations.filter(r => r.status === 'approved').length,
    fnfProcessed: resignations.filter(r => r.fnf_processed).length,
    upcomingExits: resignations.filter(r => {
      const lwd = new Date(r.last_working_date);
      const now = new Date();
      return lwd > now && lwd <= new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    }).length
  };

  const handleSubmitResignation = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/exit-management/resignation`, resignationData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resignation submitted successfully!');
      setShowResignationForm(false);
      resetResignationForm();
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitClearance = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/exit-management/clearance`, clearanceData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Exit clearance submitted!');
      setShowClearanceForm(false);
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleProcessFnF = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Calculate gross and net
      const gross = parseFloat(fnfData.basic_salary_days || 0) + 
                   parseFloat(fnfData.hra_days || 0) +
                   parseFloat(fnfData.other_allowances || 0) +
                   parseFloat(fnfData.pending_salary || 0) +
                   parseFloat(fnfData.leave_encashment || 0) +
                   parseFloat(fnfData.bonus_if_any || 0);
      
      const deductions = parseFloat(fnfData.notice_shortfall_recovery || 0) +
                        parseFloat(fnfData.advance_recovery || 0) +
                        parseFloat(fnfData.loan_recovery || 0) +
                        parseFloat(fnfData.asset_damage_recovery || 0) +
                        parseFloat(fnfData.other_deductions || 0);
      
      const net = gross - deductions;

      const payload = {
        ...fnfData,
        gross_amount: gross,
        total_deductions: deductions,
        net_payable: net
      };

      await axios.post(`${API}/exit-management/full-and-final`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('F&F processed successfully!');
      setShowFnfForm(false);
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const resetResignationForm = () => {
    setResignationData({
      employee_id: '',
      resignation_date: new Date().toISOString().split('T')[0],
      last_working_date: '',
      reason: '',
      notice_period_days: 30,
      is_notice_served: true,
      notice_buyout: false,
      notice_buyout_amount: 0
    });
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    const colors = {
      'submitted': 'bg-yellow-100 text-yellow-700 border-l-yellow-500',
      'pending': 'bg-yellow-100 text-yellow-700 border-l-yellow-500',
      'approved': 'bg-green-100 text-green-700 border-l-green-500',
      'rejected': 'bg-red-100 text-red-700 border-l-red-500',
      'withdrawn': 'bg-gray-100 text-gray-700 border-l-gray-400'
    };
    return colors[s] || colors['pending'];
  };

  const getStatusBadgeColor = (status) => {
    const s = status?.toLowerCase();
    const colors = {
      'submitted': 'bg-yellow-500 text-white',
      'pending': 'bg-yellow-500 text-white',
      'approved': 'bg-green-500 text-white',
      'rejected': 'bg-red-500 text-white',
      'withdrawn': 'bg-gray-400 text-white'
    };
    return colors[s] || colors['pending'];
  };

  const openClearanceForm = (resignation) => {
    setClearanceData({
      ...clearanceData,
      resignation_id: resignation.id,
      employee_id: resignation.employee_id
    });
    setSelectedResignation(resignation);
    setShowClearanceForm(true);
  };

  const openFnfForm = (resignation) => {
    setFnfData({
      ...fnfData,
      resignation_id: resignation.id,
      employee_id: resignation.employee_id
    });
    setSelectedResignation(resignation);
    setShowFnfForm(true);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>🚪 Exit Management</h1>
            <p className="text-gray-600 mt-2">Resignation, Exit Clearance & Full & Final Settlement</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAllData}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => { resetResignationForm(); setShowResignationForm(true); }}
              className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
              style={{backgroundColor: '#2b3380'}}
            >
              <Plus className="w-5 h-5" />
              Submit Resignation
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full" style={{backgroundColor: '#e8eaf6'}}>
              <LogOut className="w-6 h-6" style={{color: '#2b3380'}} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{stats.total}</p>
              <p className="text-sm text-gray-500">Total Resignations</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-yellow-100">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-sm text-gray-500">Pending Review</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-orange-100">
              <Calendar className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">{stats.upcomingExits}</p>
              <p className="text-sm text-gray-500">Next 30 Days</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-100">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.fnfProcessed}</p>
              <p className="text-sm text-gray-500">F&F Processed</p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by employee name, code, or department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="withdrawn">Withdrawn</option>
            </select>
          </div>
        </div>

        {/* Resignations List */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-3 text-gray-500">Loading resignations...</p>
          </div>
        ) : filteredResignations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <LogOut className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-xl font-medium text-gray-500 mb-2">No resignations found</p>
            <p className="text-gray-400">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try different search terms or filters' 
                : 'Click "Submit Resignation" to create one'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredResignations.map((resignation) => (
              <div 
                key={resignation.id} 
                className={`bg-white rounded-lg shadow-md p-6 border-l-4 hover:shadow-lg transition-shadow ${getStatusColor(resignation.status)}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold" style={{color: '#2b3380'}}>
                        {resignation.employee_name || 'Unknown Employee'}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(resignation.status)}`}>
                        {resignation.status?.toUpperCase()}
                      </span>
                      {resignation.fnf_processed && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          F&F DONE
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">{resignation.employee_code}</span>
                      {resignation.department && <span> • {resignation.department}</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Last Working Date</p>
                    <p className="text-lg font-bold" style={{color: '#2b3380'}}>
                      {resignation.last_working_date ? new Date(resignation.last_working_date).toLocaleDateString('en-IN') : 'N/A'}
                    </p>
                  </div>
                </div>
                
                {resignation.reason && (
                  <p className="text-gray-700 mb-3">
                    <span className="font-medium">Reason:</span> {resignation.reason}
                  </p>
                )}
                
                <div className="flex flex-wrap justify-between items-center text-sm text-gray-500 pt-3 border-t">
                  <div className="flex items-center gap-4">
                    <span>
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Resignation: {resignation.resignation_date ? new Date(resignation.resignation_date).toLocaleDateString('en-IN') : 'N/A'}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                      Notice: {resignation.notice_period_days || 30} days
                    </span>
                    {resignation.notice_buyout && (
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">
                        Buyout: ₹{resignation.notice_buyout_amount?.toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 mt-2 md:mt-0">
                    {resignation.status === 'approved' && !resignation.clearance_completed && (
                      <button
                        onClick={() => openClearanceForm(resignation)}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs font-medium"
                      >
                        Process Clearance
                      </button>
                    )}
                    {resignation.status === 'approved' && !resignation.fnf_processed && (
                      <button
                        onClick={() => openFnfForm(resignation)}
                        className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs font-medium"
                      >
                        Process F&F
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedResignation(resignation)}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Submit Resignation Form Modal */}
        {showResignationForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>🚪 Submit Resignation</h2>
                  <button onClick={() => setShowResignationForm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmitResignation}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Employee *</label>
                      <select
                        required
                        value={resignationData.employee_id}
                        onChange={(e) => setResignationData({...resignationData, employee_id: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Employee</option>
                        {employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.first_name} {emp.last_name} ({emp.employee_id})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Resignation Date *</label>
                        <input
                          type="date"
                          required
                          value={resignationData.resignation_date}
                          onChange={(e) => setResignationData({...resignationData, resignation_date: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Last Working Date *</label>
                        <input
                          type="date"
                          required
                          value={resignationData.last_working_date}
                          onChange={(e) => setResignationData({...resignationData, last_working_date: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Notice Period (Days) *</label>
                      <input
                        type="number"
                        required
                        value={resignationData.notice_period_days}
                        onChange={(e) => setResignationData({...resignationData, notice_period_days: parseInt(e.target.value)})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Reason for Leaving *</label>
                      <textarea
                        required
                        value={resignationData.reason}
                        onChange={(e) => setResignationData({...resignationData, reason: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Please provide the reason for resignation..."
                      />
                    </div>

                    <div className="flex gap-6">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={resignationData.is_notice_served}
                          onChange={(e) => setResignationData({...resignationData, is_notice_served: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">Notice Served</span>
                      </label>

                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={resignationData.notice_buyout}
                          onChange={(e) => setResignationData({...resignationData, notice_buyout: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">Notice Buyout</span>
                      </label>
                    </div>

                    {resignationData.notice_buyout && (
                      <div>
                        <label className="block text-sm font-medium mb-2">Buyout Amount (₹)</label>
                        <input
                          type="number"
                          value={resignationData.notice_buyout_amount}
                          onChange={(e) => setResignationData({...resignationData, notice_buyout_amount: parseFloat(e.target.value)})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-700">
                        <strong>Note:</strong> This resignation will be submitted for review. HR will process the exit clearance and F&F settlement.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
                        style={{backgroundColor: submitting ? '#9e9e9e' : '#2b3380'}}
                      >
                        <LogOut className="w-5 h-5" />
                        {submitting ? 'Submitting...' : 'Submit Resignation'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowResignationForm(false)}
                        className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Exit Clearance Form Modal */}
        {showClearanceForm && selectedResignation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>📋 Exit Clearance</h2>
                  <button onClick={() => setShowClearanceForm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="font-medium">{selectedResignation.employee_name}</p>
                  <p className="text-sm text-gray-500">LWD: {selectedResignation.last_working_date}</p>
                </div>

                <form onSubmit={handleSubmitClearance}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Asset Return Status *</label>
                      <select
                        value={clearanceData.asset_return_status}
                        onChange={(e) => setClearanceData({...clearanceData, asset_return_status: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="partial">Partial</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Asset Return Notes</label>
                      <textarea
                        value={clearanceData.asset_return_notes}
                        onChange={(e) => setClearanceData({...clearanceData, asset_return_notes: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows="2"
                        placeholder="List assets returned or pending..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Dues Amount (₹)</label>
                        <input
                          type="number"
                          value={clearanceData.dues_amount}
                          onChange={(e) => setClearanceData({...clearanceData, dues_amount: parseFloat(e.target.value)})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Recoveries (₹)</label>
                        <input
                          type="number"
                          value={clearanceData.recoveries}
                          onChange={(e) => setClearanceData({...clearanceData, recoveries: parseFloat(e.target.value)})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="flex gap-6">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={clearanceData.dues_cleared}
                          onChange={(e) => setClearanceData({...clearanceData, dues_cleared: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">Dues Cleared</span>
                      </label>

                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={clearanceData.exit_interview_completed}
                          onChange={(e) => setClearanceData({...clearanceData, exit_interview_completed: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">Exit Interview Completed</span>
                      </label>
                    </div>

                    {clearanceData.exit_interview_completed && (
                      <div>
                        <label className="block text-sm font-medium mb-2">Exit Interview Notes</label>
                        <textarea
                          value={clearanceData.exit_interview_notes}
                          onChange={(e) => setClearanceData({...clearanceData, exit_interview_notes: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          rows="3"
                          placeholder="Key feedback from exit interview..."
                        />
                      </div>
                    )}

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
                        style={{backgroundColor: submitting ? '#9e9e9e' : '#2b3380'}}
                      >
                        <CheckCircle className="w-5 h-5" />
                        {submitting ? 'Submitting...' : 'Submit Clearance'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowClearanceForm(false)}
                        className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* F&F Form Modal */}
        {showFnfForm && selectedResignation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>💰 Full & Final Settlement</h2>
                  <button onClick={() => setShowFnfForm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="font-medium">{selectedResignation.employee_name}</p>
                  <p className="text-sm text-gray-500">LWD: {selectedResignation.last_working_date}</p>
                </div>

                <form onSubmit={handleProcessFnF}>
                  <div className="space-y-6">
                    {/* Earnings */}
                    <div>
                      <h3 className="font-medium text-green-700 mb-3 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" /> Earnings
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Basic Salary (₹)</label>
                          <input
                            type="number"
                            value={fnfData.basic_salary_days}
                            onChange={(e) => setFnfData({...fnfData, basic_salary_days: parseFloat(e.target.value) || 0})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">HRA (₹)</label>
                          <input
                            type="number"
                            value={fnfData.hra_days}
                            onChange={(e) => setFnfData({...fnfData, hra_days: parseFloat(e.target.value) || 0})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Other Allowances (₹)</label>
                          <input
                            type="number"
                            value={fnfData.other_allowances}
                            onChange={(e) => setFnfData({...fnfData, other_allowances: parseFloat(e.target.value) || 0})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Pending Salary (₹)</label>
                          <input
                            type="number"
                            value={fnfData.pending_salary}
                            onChange={(e) => setFnfData({...fnfData, pending_salary: parseFloat(e.target.value) || 0})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Leave Encashment (₹)</label>
                          <input
                            type="number"
                            value={fnfData.leave_encashment}
                            onChange={(e) => setFnfData({...fnfData, leave_encashment: parseFloat(e.target.value) || 0})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Bonus (₹)</label>
                          <input
                            type="number"
                            value={fnfData.bonus_if_any}
                            onChange={(e) => setFnfData({...fnfData, bonus_if_any: parseFloat(e.target.value) || 0})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Deductions */}
                    <div>
                      <h3 className="font-medium text-red-700 mb-3 flex items-center gap-2">
                        <XCircle className="w-5 h-5" /> Deductions
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Notice Shortfall (₹)</label>
                          <input
                            type="number"
                            value={fnfData.notice_shortfall_recovery}
                            onChange={(e) => setFnfData({...fnfData, notice_shortfall_recovery: parseFloat(e.target.value) || 0})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Advance Recovery (₹)</label>
                          <input
                            type="number"
                            value={fnfData.advance_recovery}
                            onChange={(e) => setFnfData({...fnfData, advance_recovery: parseFloat(e.target.value) || 0})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Loan Recovery (₹)</label>
                          <input
                            type="number"
                            value={fnfData.loan_recovery}
                            onChange={(e) => setFnfData({...fnfData, loan_recovery: parseFloat(e.target.value) || 0})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Asset Damage (₹)</label>
                          <input
                            type="number"
                            value={fnfData.asset_damage_recovery}
                            onChange={(e) => setFnfData({...fnfData, asset_damage_recovery: parseFloat(e.target.value) || 0})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Other Deductions (₹)</label>
                          <input
                            type="number"
                            value={fnfData.other_deductions}
                            onChange={(e) => setFnfData({...fnfData, other_deductions: parseFloat(e.target.value) || 0})}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between mb-2">
                        <span className="text-green-700">Gross Earnings:</span>
                        <span className="font-bold text-green-700">
                          ₹{(
                            (parseFloat(fnfData.basic_salary_days) || 0) +
                            (parseFloat(fnfData.hra_days) || 0) +
                            (parseFloat(fnfData.other_allowances) || 0) +
                            (parseFloat(fnfData.pending_salary) || 0) +
                            (parseFloat(fnfData.leave_encashment) || 0) +
                            (parseFloat(fnfData.bonus_if_any) || 0)
                          ).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between mb-2">
                        <span className="text-red-700">Total Deductions:</span>
                        <span className="font-bold text-red-700">
                          ₹{(
                            (parseFloat(fnfData.notice_shortfall_recovery) || 0) +
                            (parseFloat(fnfData.advance_recovery) || 0) +
                            (parseFloat(fnfData.loan_recovery) || 0) +
                            (parseFloat(fnfData.asset_damage_recovery) || 0) +
                            (parseFloat(fnfData.other_deductions) || 0)
                          ).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t">
                        <span className="font-bold" style={{color: '#2b3380'}}>Net Payable:</span>
                        <span className="font-bold text-xl" style={{color: '#2b3380'}}>
                          ₹{(
                            (parseFloat(fnfData.basic_salary_days) || 0) +
                            (parseFloat(fnfData.hra_days) || 0) +
                            (parseFloat(fnfData.other_allowances) || 0) +
                            (parseFloat(fnfData.pending_salary) || 0) +
                            (parseFloat(fnfData.leave_encashment) || 0) +
                            (parseFloat(fnfData.bonus_if_any) || 0) -
                            (parseFloat(fnfData.notice_shortfall_recovery) || 0) -
                            (parseFloat(fnfData.advance_recovery) || 0) -
                            (parseFloat(fnfData.loan_recovery) || 0) -
                            (parseFloat(fnfData.asset_damage_recovery) || 0) -
                            (parseFloat(fnfData.other_deductions) || 0)
                          ).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
                        style={{backgroundColor: submitting ? '#9e9e9e' : '#2b3380'}}
                      >
                        <DollarSign className="w-5 h-5" />
                        {submitting ? 'Processing...' : 'Process F&F'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowFnfForm(false)}
                        className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Details Modal */}
        {selectedResignation && !showClearanceForm && !showFnfForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>Resignation Details</h2>
                  <button onClick={() => setSelectedResignation(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Employee:</span>
                    <span className="font-medium">{selectedResignation.employee_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Employee Code:</span>
                    <span className="font-medium">{selectedResignation.employee_code}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Department:</span>
                    <span className="font-medium">{selectedResignation.department || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeColor(selectedResignation.status)}`}>
                      {selectedResignation.status?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Resignation Date:</span>
                    <span className="font-medium">{selectedResignation.resignation_date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Last Working Date:</span>
                    <span className="font-medium">{selectedResignation.last_working_date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Notice Period:</span>
                    <span className="font-medium">{selectedResignation.notice_period_days} days</span>
                  </div>
                  {selectedResignation.reason && (
                    <div>
                      <span className="text-gray-500">Reason:</span>
                      <p className="mt-1 text-gray-700">{selectedResignation.reason}</p>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">F&F Processed:</span>
                    <span className="font-medium">{selectedResignation.fnf_processed ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t">
                  <button
                    onClick={() => setSelectedResignation(null)}
                    className="w-full px-6 py-3 border rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ExitManagement;
